// pages/admin/PayoutsPage.tsx
// Payout Management Page - Admin Portal

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import socketService from '../../services/socketService';
import soundService from '../../services/soundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock, faThumbsUp, faCheckCircle, faChartBar, faTimesCircle, faBan,
  faArrowRightArrowLeft, faRotate, faTimes, faBuilding,
  faPaperclip, faEye, faGlobe, faDesktop, faUniversity,
  faCloudUpload, faImage, faSpinner, faSearch, faSortAmountDown
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { LiveViewToggle, LiveModeBufferBadge } from '../../components/shared/LiveViewToggle';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import bankLogos from 'thai-banks-logo/banks-logo.json';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const SEARCH_INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7';
const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TEXTAREA_CLASS = `${INPUT_CLASS} resize-none`;
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const bankNameToLogoKey: Record<string, string> = {
  KBANK: 'KBANK', KTB: 'KTB', SCB: 'SCB', BBL: 'BBL', BAY: 'BAY',
  TTB: 'TTB', TMB: 'TTB', GSB: 'GSB', GHB: 'GHB', BAAC: 'BAAC',
  CIMB: 'CIMB', CIMBT: 'CIMB', UOB: 'UOB', UOBT: 'UOB', TISCO: 'TISCO',
  KKP: 'KKP', KKB: 'KKP', LHB: 'LHB', 'LH BANK': 'LHB', ICBC: 'ICBC',
  TCRB: 'TCRB', HSBC: 'HSBC', CITI: 'CITI', IBANK: 'IBANK', ISBT: 'IBANK',
};

const PAYOUT_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100',  text: 'text-amber-700',  icon: faClock,       label: 'รอการอนุมัติ' },
  approved:  { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: faThumbsUp,    label: 'อนุมัติแล้ว' },
  rejected:  { bg: 'bg-red-100',    text: 'text-red-600',    icon: faTimesCircle, label: 'ปฏิเสธ' },
  completed: { bg: 'bg-green-100',  text: 'text-green-700',  icon: faCheckCircle, label: 'เสร็จสิ้น' },
  cancelled: { bg: 'bg-gray-100',   text: 'text-gray-600',   icon: faBan,         label: 'ยกเลิก' },
};

interface Payout {
  id: string;
  payout_number: string;
  reference_id?: string;
  amount: number;
  fee_amount: number;
  net_amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  slip_url?: string;
  note?: string;
  rejected_reason?: string;
  approved_at?: string;
  completed_at?: string;
  created_at: string;
  created_via: 'portal' | 'api';
  approved_by_name?: string;
  merchant: { id: string; name: string; email: string };
  transfer_response?: {
    amount: number;
    from_account: string;
    status: string;
    success: boolean;
    to_account: string;
    to_bank: string;
    transfer_order_id: string;
    tx_ref: string;
  };
}

interface Stats {
  pending: number;
  approved: number;
  completed: number;
  rejected: number;
  cancelled: number;
  total: number;
  total_amount: number;
  completed_amount: number;
  pending_amount: number;
}

type ModalType = 'approve' | 'approve_auto' | 'reject' | 'complete';

const AdminPayoutsPage: React.FC = () => {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('approve');
  const [modalData, setModalData] = useState({ note: '', rejected_reason: '', slip_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [merchants, setMerchants] = useState<{ id: string; name: string; email: string }[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedFromAccount, setSelectedFromAccount] = useState<string>('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [withdrawBanks, setWithdrawBanks] = useState<any[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [needsBankSelection, setNeedsBankSelection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [realtimeMode, setRealtimeMode] = useState(true);
  const [bufferedNewPayouts, setBufferedNewPayouts] = useState(0);

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdminEmployee = adminUser.type === 'admin_employee';
  const canApprove = isAdminEmployee ? adminUser.permissions?.approve_payouts === true : true;

  const loadData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedMerchantId) params.append('merchant_id', selectedMerchantId);
      params.append('sort_order', sortOrder);
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));

      const statsParams = new URLSearchParams();
      if (statusFilter) statsParams.append('status', statusFilter);
      if (appliedDateFrom) statsParams.append('start_date', appliedDateFrom);
      if (appliedDateTo) statsParams.append('end_date', appliedDateTo);
      if (searchTerm) statsParams.append('search', searchTerm);
      if (selectedMerchantId) statsParams.append('merchant_id', selectedMerchantId);

      const [payoutsRes, statsRes] = await Promise.all([
        merchantApi.get(`/admin/payouts?${params.toString()}`),
        merchantApi.get(`/admin/payouts/stats?${statsParams.toString()}`)
      ]);

      if (payoutsRes.data.success) {
        setPayouts(payoutsRes.data.data.payouts || []);
        setTotalItems(payoutsRes.data.data.total || 0);
      }
      if (statsRes.data.success) setStats(statsRes.data.data);
    } catch (error: any) {
      console.error('Error loading payouts:', error);
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedDateFrom, appliedDateTo, searchTerm, selectedMerchantId, sortOrder, currentPage, pageSize]);

  const loadStats = useCallback(async () => {
    try {
      const statsParams = new URLSearchParams();
      if (statusFilter) statsParams.append('status', statusFilter);
      if (appliedDateFrom) statsParams.append('start_date', appliedDateFrom);
      if (appliedDateTo) statsParams.append('end_date', appliedDateTo);
      if (searchTerm) statsParams.append('search', searchTerm);
      if (selectedMerchantId) statsParams.append('merchant_id', selectedMerchantId);
      const res = await merchantApi.get(`/admin/payouts/stats?${statsParams.toString()}`);
      if (res.data.success) setStats(res.data.data);
    } catch {}
  }, [statusFilter, appliedDateFrom, appliedDateTo, searchTerm, selectedMerchantId]);

  const loadStatsRef = useRef(loadStats);
  useEffect(() => { loadStatsRef.current = loadStats; }, [loadStats]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useEffect(() => {
    soundService.loadSettings();
    merchantApi.get('/merchants').then(res => {
      setMerchants(res.data.data || []);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handlePayoutCreated = (data: any) => {
      // ⭐ P2P liquidity payout ไม่เกี่ยวกับหน้านี้ (จัดการที่หน้า P2P Payouts) — payload ย่อจาก p2pPayoutService ไม่มี merchant object, ปล่อยเข้า list แล้ว crash
      if (data?.is_p2p_liquidity) return;
      soundService.playWithdrawalSound();
      if (realtimeMode) {
        setCurrentPage(prevPage => {
          if (prevPage !== 1) return prevPage;
          setPayouts(prev => {
            const statusOk = !statusFilter || statusFilter === 'pending';
            const searchOk = !searchTerm;
            if (!statusOk || !searchOk) return prev;
            if (prev.some(p => p.id === data.id)) return prev.map(p => p.id === data.id ? data : p);
            return sortOrder === 'desc'
              ? [data, ...prev.slice(0, pageSize - 1)]
              : [...prev.slice(0, pageSize - 1), data];
          });
          return prevPage;
        });
        setTotalItems(prev => prev + 1);
        setStats(prev => prev ? {
          ...prev,
          pending: prev.pending + 1,
          total: prev.total + 1,
          total_amount: prev.total_amount + (data.amount || 0),
          pending_amount: prev.pending_amount + (data.amount || 0),
        } : prev);
      } else {
        setBufferedNewPayouts(prev => prev + 1);
      }
    };

    const handlePayoutUpdated = (data: any) => {
      if (realtimeMode) {
        if (data.payout_id) {
          setPayouts(prev => prev.map(p =>
            p.id === data.payout_id
              ? { ...p, status: data.status ?? p.status, approved_by_name: data.approved_by ?? p.approved_by_name, rejected_reason: data.rejected_reason ?? p.rejected_reason, slip_url: data.slip_url ?? p.slip_url }
              : p
          ));
        }
        loadStatsRef.current();
      } else {
        setBufferedNewPayouts(prev => prev + 1);
      }
    };

    socketService.on('payout-created', handlePayoutCreated);
    socketService.on('payout-updated', handlePayoutUpdated);
    return () => {
      socketService.off('payout-created', handlePayoutCreated);
      socketService.off('payout-updated', handlePayoutUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchTerm, pageSize, sortOrder, realtimeMode]);

  const openModal = async (type: ModalType, payout: Payout) => {
    setModalType(type);
    setSelectedPayout(payout);
    setModalData({ note: '', rejected_reason: '', slip_url: '' });
    setSelectedFile(null);
    setPreviewUrl('');
    setUploadProgress(0);
    setBankAccounts([]);
    setSelectedFromAccount('');
    setWithdrawBanks([]);
    setSelectedBankId('');
    setNeedsBankSelection(false);
    setShowModal(true);
    if (type === 'approve_auto') await fetchWithdrawBanks();
  };

  const fetchWithdrawBanks = async () => {
    setLoadingAccounts(true);
    try {
      const response = await merchantApi.get('/admin/payouts/withdraw-banks');
      if (response.data.success) {
        const banks = response.data.data.banks || [];
        if (banks.length === 0) {
          toast.error('ไม่พบธนาคารที่ตั้งค่าสำหรับถอนเงินอัตโนมัติ');
          setNeedsBankSelection(false);
        } else if (banks.length === 1) {
          setNeedsBankSelection(false);
          setWithdrawBanks(banks);
          await fetchBankAccounts(banks[0].id);
        } else {
          setNeedsBankSelection(true);
          setWithdrawBanks(banks);
        }
      } else {
        toast.error(response.data.message || 'ไม่สามารถดึงรายการธนาคารได้');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'ไม่สามารถดึงรายการธนาคารได้');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchBankAccounts = async (bankId: string) => {
    setLoadingAccounts(true);
    try {
      const response = await merchantApi.post('/admin/payouts/bank-accounts', { bank_id: bankId });
      if (response.data.success) {
        const data = response.data.data;
        setNeedsBankSelection(false);
        setBankAccounts(data.accounts || []);
        setSelectedBankId(data.bank_id);
      } else {
        toast.error(response.data.message || 'ไม่สามารถดึงบัญชีได้');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'ไม่สามารถดึงบัญชีได้');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleBankSelect = async (bankId: string) => {
    setSelectedBankId(bankId);
    await fetchBankAccounts(bankId);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) { toast.error('กรุณาเลือกไฟล์รูปภาพ (JPEG, PNG, WebP, GIF) เท่านั้น'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('ไฟล์มีขนาดใหญ่เกิน 5MB'); return; }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApprove = async (withdrawal_type: 'manual' | 'auto' = 'manual') => {
    if (!selectedPayout) return;
    if (withdrawal_type === 'auto' && !selectedFromAccount) { toast.error('กรุณาเลือกบัญชีต้นทางสำหรับโอนเงิน'); return; }
    try {
      setSubmitting(true);
      const res = await merchantApi.post(`/admin/payouts/${selectedPayout.id}/approve`, {
        note: modalData.note || undefined,
        withdrawal_type,
        from_account: withdrawal_type === 'auto' ? selectedFromAccount : undefined,
        bank_id: withdrawal_type === 'auto' ? selectedBankId : undefined
      });
      if (res.data.success) {
        toast.success(withdrawal_type === 'auto' ? 'อนุมัติและโอนเงินสำเร็จ' : 'อนุมัติ Payout สำเร็จ');
        setShowModal(false);
        loadData();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPayout || !modalData.rejected_reason.trim()) { toast.error('กรุณาระบุเหตุผลในการปฏิเสธ'); return; }
    try {
      setSubmitting(true);
      const res = await merchantApi.post(`/admin/payouts/${selectedPayout.id}/reject`, { rejected_reason: modalData.rejected_reason });
      if (res.data.success) { toast.success('ปฏิเสธ Payout สำเร็จ'); setShowModal(false); loadData(); }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedPayout) return;
    if (!selectedFile && !modalData.slip_url.trim()) { toast.error('กรุณาอัพโหลดสลิป หรือใส่ URL ของสลิป'); return; }
    try {
      setSubmitting(true);
      if (selectedFile) {
        const formData = new FormData();
        formData.append('slip', selectedFile);
        if (modalData.note) formData.append('note', modalData.note);
        const res = await merchantApi.post(
          `/admin/payouts/${selectedPayout.id}/complete-with-upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (progressEvent) => { const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1)); setUploadProgress(percent); } }
        );
        if (res.data.success) { toast.success('เสร็จสิ้นการ Payout สำเร็จ (อัพโหลดสลิปแล้ว)'); setShowModal(false); clearSelectedFile(); loadData(); }
      } else {
        const res = await merchantApi.post(`/admin/payouts/${selectedPayout.id}/complete`, { slip_url: modalData.slip_url, note: modalData.note || undefined });
        if (res.data.success) { toast.success('เสร็จสิ้นการ Payout สำเร็จ'); setShowModal(false); loadData(); }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  const toggleRealtimeMode = () => {
    if (!realtimeMode && bufferedNewPayouts > 0) {
      loadData();
      setBufferedNewPayouts(0);
      toast.info(`ดึงข้อมูลใหม่ พร้อมอัพเดท ${bufferedNewPayouts} รายการล่าสุด`);
    }
    setRealtimeMode((prev) => !prev);
  };

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('');
    setSelectedMerchantId('');
    setSortOrder('desc');
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setCurrentPage(1);
  };

  const statCards: StatCardDef[] = stats ? [
    {
      icon: faClock,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'รอการอนุมัติ',
      value: <span className="text-amber-600 dark:text-amber-400">{stats.pending.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{stats.pending_amount?.toLocaleString() || 0}</span>,
    },
    {
      icon: faThumbsUp,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'อนุมัติแล้ว',
      value: <span className="text-blue-600 dark:text-blue-400">{stats.approved.toLocaleString()}</span>,
    },
    {
      icon: faCheckCircle,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'เสร็จสิ้น',
      value: <span className="text-green-600 dark:text-green-400">{stats.completed.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{stats.completed_amount?.toLocaleString() || 0}</span>,
    },
    {
      icon: faChartBar,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'ทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>{stats.total.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">฿{stats.total_amount?.toLocaleString() || 0}</span>,
    },
  ] : [];

  const columns: Column<Payout>[] = [
    {
      key: 'payout_number',
      header: 'เลขที่ Payout',
      width: '150px',
      sortable: true,
      render: (payout) => (
        <div className="min-w-0">
          <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm">{payout.payout_number}</p>
          {payout.reference_id && <p className="text-xs text-gray-500 dark:text-slate-400 truncate">Ref: {payout.reference_id}</p>}
        </div>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      width: '180px',
      render: (payout) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 flex-shrink-0">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{payout.merchant.name}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{payout.merchant.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      width: '120px',
      sortable: true,
      render: (payout) => (
        <div>
          <p className="font-bold text-sm tabular-nums whitespace-nowrap" style={{ color: 'var(--theme-primary)' }}>
            ฿{payout.amount.toLocaleString()}
          </p>
          {payout.fee_amount > 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 tabular-nums">Fee: ฿{payout.fee_amount.toLocaleString()}</p>
          )}
        </div>
      ),
    },
    {
      key: 'bank_account',
      header: 'บัญชีปลายทาง',
      render: (payout) => {
        const logoKey = bankNameToLogoKey[(payout.bank_name || '').toUpperCase()] as keyof typeof bankLogos | undefined;
        const icon = logoKey ? bankLogos[logoKey]?.icon : null;
        return (
          <div className="flex items-center gap-2 min-w-0 max-w-[220px]">
            {icon ? (
              <img src={icon} alt={payout.bank_name} className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
                <FontAwesomeIcon icon={faUniversity} className="text-xs" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs text-gray-500 dark:text-slate-400">{payout.bank_name}</p>
              <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 px-1.5 py-0.5 rounded tabular-nums">{payout.bank_account_number}</code>
              <p className="text-xs text-gray-700 dark:text-slate-200 mt-0.5 truncate">{payout.bank_account_name}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'สถานะ',
      sortable: true,
      render: (payout) => (
        <StatusBadge
          status={payout.status}
          statusMap={{
            ...PAYOUT_STATUS_MAP,
            completed: {
              ...PAYOUT_STATUS_MAP.completed,
              label: payout.transfer_response ? 'เสร็จสิ้น (AUTO)' : 'เสร็จสิ้น',
            },
          }}
        />
      )
    },
    {
      key: 'created_via',
      header: 'ช่องทาง',
      width: '90px',
      render: (payout) => (
        payout.created_via === 'api' ? (
          <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded text-xs font-medium inline-flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon icon={faGlobe} className="text-[10px]" /> API
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded text-xs font-medium inline-flex items-center gap-1 whitespace-nowrap">
            <FontAwesomeIcon icon={faDesktop} className="text-[10px]" /> Portal
          </span>
        )
      ),
    },
    {
      key: 'approved_by_name',
      header: 'ทำรายการโดย',
      render: (payout) => <p className="text-sm text-gray-700 dark:text-slate-200 truncate max-w-[120px]">{payout.approved_by_name || '-'}</p>,
    },
    {
      key: 'created_at',
      header: 'วันที่',
      width: '110px',
      sortable: true,
      render: (payout) => (
        <div className="whitespace-nowrap">
          <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(payout.created_at).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(payout.created_at).format('HH:mm')}</p>
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '200px',
      render: (payout) => (
        <div className="flex items-center gap-1 flex-wrap">
          {payout.status === 'pending' && canApprove && (
            <select
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const value = e.target.value as 'approve' | 'approve_auto' | 'reject';
                if (value) { openModal(value, payout); e.target.value = ''; }
              }}
              className={`${FILTER_SELECT_CLASS} !py-1 !px-2 text-[11px] max-w-[130px]`}
              defaultValue=""
            >
              <option value="" disabled>ดำเนินการ</option>
              <option value="approve">อนุมัติ (มือ)</option>
              <option value="approve_auto">อนุมัติ (Auto)</option>
              <option value="reject">ไม่อนุมัติ</option>
            </select>
          )}
          {payout.status === 'approved' && canApprove && (
            <button
              onClick={(e) => { e.stopPropagation(); openModal('complete', payout); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faPaperclip} className="text-[10px]" /> แนบสลิป
            </button>
          )}
          {payout.slip_url && (
            <a
              href={payout.slip_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faEye} className="text-[10px]" /> สลิป
            </a>
          )}
          {payout.rejected_reason && (
            <button
              onClick={(e) => { e.stopPropagation(); alert(`เหตุผล: ${payout.rejected_reason}`); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
            >
              เหตุผล
            </button>
          )}
          {!canApprove && (payout.status === 'pending' || payout.status === 'approved') && (
            <span className="text-xs text-gray-400 dark:text-slate-500">(ไม่มีสิทธิ์)</span>
          )}
        </div>
      ),
    },
  ];

  const modalTitle = {
    approve: 'อนุมัติ Payout (ถอนมือ)',
    approve_auto: 'อนุมัติ Payout (Auto)',
    reject: 'ปฏิเสธ Payout',
    complete: 'แนบสลิปและเสร็จสิ้น',
  }[modalType];

  const modalIcon = {
    approve: faCheckCircle,
    approve_auto: faCheckCircle,
    reject: faTimesCircle,
    complete: faPaperclip,
  }[modalType];

  const modalIconColor = {
    approve: 'text-green-500',
    approve_auto: 'text-blue-500',
    reject: 'text-red-500',
    complete: 'text-blue-500',
  }[modalType];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faArrowRightArrowLeft}
        title="จัดการ Payout"
        subtitle={`คำขอถอนเงินจาก Merchant • ${totalItems.toLocaleString()} รายการ`}
        below={<LiveViewToggle realtimeMode={realtimeMode} onToggle={toggleRealtimeMode} />}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <UiButton variant="secondary" icon={faRotate} onClick={() => loadData()}>
            Refresh
          </UiButton>
        }
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="เลข Payout, ข้อมูลบัญชี..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={SEARCH_INPUT_CLASS}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className={FILTER_SELECT_CLASS}
          >
            <option value="">ทุกสถานะ</option>
            <option value="pending">รอการอนุมัติ</option>
            <option value="approved">อนุมัติแล้ว</option>
            <option value="completed">เสร็จสิ้น</option>
            <option value="rejected">ปฏิเสธ</option>
            <option value="cancelled">ยกเลิก</option>
          </select>
        </div>

        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Merchant</label>
          <MerchantMultiSelect
            merchants={merchants}
            value={selectedMerchantId}
            onChange={(v) => { setSelectedMerchantId(v); setCurrentPage(1); }}
            variant="light"
          />
        </div>

        <div className="min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSortAmountDown} className="mr-1" />
            เรียงลำดับ
          </label>
          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value as 'desc' | 'asc'); setCurrentPage(1); }}
            className={FILTER_SELECT_CLASS}
          >
            <option value="desc">ใหม่สุดก่อน</option>
            <option value="asc">เก่าสุดก่อน</option>
          </select>
        </div>
      </UiFilterPanel>

      <div className="flex justify-end mb-2">
        <LiveModeBufferBadge
          count={bufferedNewPayouts}
          onFlush={() => { loadData(); setBufferedNewPayouts(0); toast.success('อัพเดทข้อมูลแล้ว'); }}
        />
      </div>

      <UiStatsGrid cards={statCards} loading={loading && !stats} cols={4} />

      <DataTable
        data={payouts}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีรายการ Payout"
        emptyIcon={<FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: (newPageSize) => { setPageSize(newPageSize); setCurrentPage(1); },
        }}
        rowClassName={(p) => p.status === 'pending' ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}
        hoverable
        compact
      />

      {/* Modal */}
      <UiModal
        open={showModal && !!selectedPayout}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        icon={modalIcon}
        iconColor={modalIconColor}
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowModal(false)} disabled={submitting} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              variant={modalType === 'reject' ? 'danger' : 'primary'}
              loading={submitting}
              onClick={() => {
                if (modalType === 'approve') handleApprove('manual');
                else if (modalType === 'approve_auto') handleApprove('auto');
                else if (modalType === 'reject') handleReject();
                else if (modalType === 'complete') handleComplete();
              }}
              className={`flex-1 justify-center ${modalType !== 'reject' ? '!bg-green-600 hover:!bg-green-700' : ''}`}
            >
              {uploadProgress > 0 ? `อัพโหลด ${uploadProgress}%...` : 'ยืนยัน'}
            </UiButton>
          </div>
        }
      >
        {selectedPayout && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">เลขที่:</span>
                <strong className="text-gray-800 dark:text-slate-100">{selectedPayout.payout_number}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Merchant:</span>
                <span className="text-gray-800 dark:text-slate-200">{selectedPayout.merchant.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">จำนวนเงิน:</span>
                <strong className="text-base tabular-nums" style={{ color: 'var(--theme-primary)' }}>฿{selectedPayout.amount.toLocaleString()}</strong>
              </div>
              {selectedPayout.fee_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-slate-400">ค่าธรรมเนียม:</span>
                  <span className="text-amber-600 dark:text-amber-400 font-medium tabular-nums">฿{selectedPayout.fee_amount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">บัญชี:</span>
                <span className="text-gray-800 dark:text-slate-200">{selectedPayout.bank_name} - {selectedPayout.bank_account_number}</span>
              </div>
            </div>

            {modalType === 'reject' && (
              <div>
                <label htmlFor="reject-reason" className={LABEL_CLASS}>เหตุผลในการปฏิเสธ *</label>
                <textarea
                  id="reject-reason"
                  value={modalData.rejected_reason}
                  onChange={(e) => setModalData({ ...modalData, rejected_reason: e.target.value })}
                  className={TEXTAREA_CLASS}
                  rows={3}
                  placeholder="ระบุเหตุผล..."
                  required
                />
              </div>
            )}

            {modalType === 'approve_auto' && (
              <div className="space-y-3">
                {needsBankSelection && (
                  <>
                    <p className={LABEL_CLASS}>
                      <FontAwesomeIcon icon={faUniversity} className="mr-1 normal-case text-purple-500" />
                      เลือกธนาคารสำหรับถอนเงิน *
                    </p>
                    {loadingAccounts ? (
                      <div className="flex items-center justify-center py-4 text-gray-500 dark:text-slate-400">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        กำลังโหลด...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {withdrawBanks.map((bank: any) => (
                          <button
                            key={bank.id}
                            type="button"
                            onClick={() => handleBankSelect(bank.id)}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                              selectedBankId === bank.id
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                                : 'border-gray-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            <div className="font-medium text-gray-800 dark:text-slate-100">{bank.name}</div>
                            <div className="text-sm text-gray-500 dark:text-slate-400">{bank.bank_code}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {!needsBankSelection && (
                  <>
                    <p className={LABEL_CLASS}>
                      <FontAwesomeIcon icon={faUniversity} className="mr-1 normal-case text-blue-500" />
                      เลือกบัญชีต้นทางสำหรับโอนเงิน *
                    </p>
                    {loadingAccounts ? (
                      <div className="flex items-center justify-center py-8 text-gray-500 dark:text-slate-400">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        กำลังดึงข้อมูลบัญชี...
                      </div>
                    ) : bankAccounts.length === 0 ? (
                      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl p-4 text-sm text-center">
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                        ไม่พบบัญชีที่สามารถใช้โอนเงินได้
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {bankAccounts.map((account: any) => (
                          <label
                            key={account.accountRefId}
                            className={`block p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              selectedFromAccount === account.accountNo
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="radio"
                                name="fromAccount"
                                value={account.accountNo}
                                checked={selectedFromAccount === account.accountNo}
                                onChange={(e) => setSelectedFromAccount(e.target.value)}
                                className="mt-1 w-4 h-4 text-blue-600"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-800 dark:text-slate-100">{account.accountNo}</span>
                                  <span className={`text-sm font-semibold tabular-nums ${account.availableBalance >= (selectedPayout?.amount || 0) ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                    ฿{account.availableBalance?.toLocaleString()}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-500 mt-1">{account.accountName}</div>
                                <div className="text-xs text-gray-400 mt-0.5">{account.accountType?.label} • {account.currency}</div>
                                {account.availableBalance < (selectedPayout?.amount || 0) && (
                                  <div className="text-xs text-red-500 mt-1">
                                    ⚠️ ยอดเงินไม่เพียงพอ (ต้องการ ฿{selectedPayout?.amount?.toLocaleString()})
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {modalType === 'complete' && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="slip-upload" className="block text-sm font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={faCloudUpload} className="mr-1" />
                    อัพโหลดสลิป *
                  </label>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer
                      ${previewUrl ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      id="slip-upload"
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {previewUrl ? (
                      <div className="space-y-3">
                        <div className="relative inline-block">
                          <img src={previewUrl} alt="Preview" className="max-h-40 rounded-lg mx-auto shadow-md" />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); clearSelectedFile(); }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                          >
                            <FontAwesomeIcon icon={faTimes} className="text-xs" />
                          </button>
                        </div>
                        <p className="text-sm text-green-600 font-medium">
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                          {selectedFile?.name} ({((selectedFile?.size || 0) / 1024).toFixed(1)} KB)
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                          <FontAwesomeIcon icon={faImage} className="text-gray-400 text-xl" />
                        </div>
                        <p className="text-gray-600 text-sm">คลิกเพื่อเลือกไฟล์ หรือลากมาวางที่นี่</p>
                        <p className="text-xs text-gray-400">รองรับ JPEG, PNG, WebP, GIF (ไม่เกิน 5MB)</p>
                      </div>
                    )}
                  </div>
                  {submitting && uploadProgress > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>กำลังอัพโหลด...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}
                </div>
                {!selectedFile && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400">หรือใส่ URL</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <input
                      type="url"
                      value={modalData.slip_url}
                      onChange={(e) => setModalData({ ...modalData, slip_url: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      placeholder="https://..."
                    />
                  </div>
                )}
              </div>
            )}

            {(modalType === 'approve' || modalType === 'approve_auto' || modalType === 'complete') && (
              <div>
                <label htmlFor="payout-note" className={LABEL_CLASS}>หมายเหตุ</label>
                <textarea
                  id="payout-note"
                  value={modalData.note}
                  onChange={(e) => setModalData({ ...modalData, note: e.target.value })}
                  className={TEXTAREA_CLASS}
                  rows={2}
                  placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
                />
              </div>
            )}
          </>
        )}
      </UiModal>
    </div>
  );
};

export default AdminPayoutsPage;
